import { Text, View } from 'react-native'
import Animated, {
  Easing,
  Extrapolation,
  interpolate,
  useAnimatedStyle,
  useDerivedValue,
  withDelay,
  withTiming,
} from 'react-native-reanimated'
import { Image } from 'expo-image'

import { APP_TOP_BAR_ROW_HEIGHT } from '../../../constants/app-top-bar-layout'
import { quote3 } from '../../../theme/typography'
import { buildContentfulImageUrl } from '../../../utils/contentful-image'
import { useDashboardDailyTip } from '../hooks/useDashboardDailyTip'
import { useDashboardTipVisibility } from '../hooks/useDashboardTipVisibility'
import {
  ARC_HEIGHT,
  DAILY_TIP_HEADER_COLLAPSED_HEIGHT,
  DAILY_TIP_HEADER_EXPANDED_HEIGHT,
} from './dashboard-layout-constants'

function quoteWrapperStyle(collapseProgress: number) {
  'worklet'
  return {
    maxHeight: interpolate(
      collapseProgress,
      [0, 0.52, 1],
      [0, 0, 140],
      Extrapolation.CLAMP,
    ),
    marginBottom: interpolate(
      collapseProgress,
      [0, 0.52, 1],
      [0, 0, 16],
      Extrapolation.CLAMP,
    ),
    overflow: 'hidden' as const,
  }
}

function headerStyle(heightValue: number) {
  'worklet'
  return { height: heightValue }
}

function contentStyle(collapseProgress: number) {
  'worklet'
  return {
    marginBottom: interpolate(
      collapseProgress,
      [0, 1],
      [0, ARC_HEIGHT],
      Extrapolation.CLAMP,
    ),
  }
}

function imageLayerStyle(collapseProgress: number) {
  'worklet'
  return {
    opacity: collapseProgress,
  }
}

export function DashboardHeader({ topInset }: { topInset: number }) {
  const { showTip: isExpanded } = useDashboardTipVisibility()
  const { todaysTip } = useDashboardDailyTip()

  const imageUrl = buildContentfulImageUrl(
    todaysTip?.picture?.url,
    todaysTip?.picture?.contentType ?? '',
    'full-width',
  )
  const tipText = todaysTip?.tip ?? ''
  const hasTipContent = todaysTip != null && tipText.trim() !== ''

  const collapseProgress = useDerivedValue(() => {
    const target = isExpanded ? 1 : 0
    return withTiming(target, {
      duration: isExpanded ? 420 : 240,
      easing: isExpanded
        ? Easing.bezier(0.22, 1, 0.36, 1)
        : Easing.out(Easing.cubic),
    })
  })
  const quoteProgress = useDerivedValue(() =>
    isExpanded && hasTipContent
      ? withDelay(
          120,
          withTiming(1, {
            duration: 260,
            easing: Easing.out(Easing.cubic),
          }),
        )
      : withTiming(0, {
          duration: 120,
          easing: Easing.out(Easing.quad),
        }),
  )

  const headerHeight = useDerivedValue(() =>
    interpolate(
      collapseProgress.value,
      [0, 1],
      [
        DAILY_TIP_HEADER_COLLAPSED_HEIGHT + topInset,
        DAILY_TIP_HEADER_EXPANDED_HEIGHT + topInset,
      ],
      Extrapolation.CLAMP,
    ),
  )

  const quoteAnimatedStyle = useAnimatedStyle(() => ({
    opacity: quoteProgress.value,
    transform: [
      {
        translateY: interpolate(
          quoteProgress.value,
          [0, 1],
          [12, 0],
          Extrapolation.CLAMP,
        ),
      },
      {
        scale: interpolate(
          quoteProgress.value,
          [0, 1],
          [0.98, 1],
          Extrapolation.CLAMP,
        ),
      },
    ],
  }))

  const quoteWrapperAnimatedStyle = useAnimatedStyle(() =>
    quoteWrapperStyle(collapseProgress.value),
  )

  const headerAnimatedStyle = useAnimatedStyle(() =>
    headerStyle(headerHeight.value),
  )

  const contentAnimatedStyle = useAnimatedStyle(() =>
    contentStyle(collapseProgress.value),
  )

  const imageLayerAnimatedStyle = useAnimatedStyle(() =>
    imageLayerStyle(collapseProgress.value),
  )

  return (
    <Animated.View
      className="relative w-full overflow-hidden bg-background-primary"
      style={headerAnimatedStyle}
    >
      <Animated.View
        className="absolute top-0 left-0 right-0 pointer-events-none"
        style={imageLayerAnimatedStyle}
      >
        <Image
          source={
            imageUrl
              ? { uri: imageUrl }
              : require('../../../../assets/images/onboarding-hero.jpg')
          }
          style={{
            width: '100%',
            height: DAILY_TIP_HEADER_EXPANDED_HEIGHT + topInset,
          }}
          contentFit="cover"
          contentPosition="top"
          transition={imageUrl ? 200 : undefined}
        />
        <View
          className="absolute top-0 left-0 right-0 pointer-events-none"
          style={{
            height: DAILY_TIP_HEADER_EXPANDED_HEIGHT + topInset,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
          }}
        />
      </Animated.View>

      {hasTipContent && (
        <Animated.View
          className="flex-1 items-center z-10"
          style={[
            {
              pointerEvents: 'box-none',
              marginTop: APP_TOP_BAR_ROW_HEIGHT + topInset,
            },
            contentAnimatedStyle,
          ]}
        >
          <View className="flex-1 w-full items-center justify-center px-13">
            <Animated.View style={quoteWrapperAnimatedStyle}>
              <Animated.View style={quoteAnimatedStyle}>
                <Text
                  className="text-background-secondary text-center"
                  style={quote3}
                >
                  {tipText}
                </Text>
              </Animated.View>
            </Animated.View>
          </View>
        </Animated.View>
      )}
    </Animated.View>
  )
}
